<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAppStore } from '@/stores/appStore';
import { useShellStore } from '@/stores/shellStore';
import { getSsoScope } from '@/utils/ssoSession';
import type { ModuleConfig, ResolvedSidebarItem } from '@/config/moduleRegistry';

/**
 * Labeled sidebar: categories as headings with their modules listed, standalone modules as items, a collapsed
 * 64 px mode with hover labels. Same inputs as the old icon rail (tenant `sidebar` config resolved by the app
 * store, filtered by the user's permissions).
 *
 * 2026-09-10 shell feedback: the account block lives at the foot again (avatar + label, menu opening upward
 * with the account modules and actions), so `submenu-action` is emitted from here rather than the top bar.
 * Account modules already listed in the categories above are not repeated in the menu.
 *
 * Round 4: the top bar's hamburger is gone ("the hamburger to close it is not needed, we have the collapse
 * button on the main menu anyway"), so the `.toggle` at the foot of this column is the only control of
 * `shell.collapsed`. The root `<nav>` keeps `id="app-sidebar"` — nothing points `aria-controls` at it any
 * more, but it is a harmless, stable handle on the landmark. It must stay the component's single root node —
 * a sibling comment would make it a fragment and `w.classes()` would stop seeing `collapsed`.
 */
const props = withDefaults(defineProps<{ user?: Record<string, any> | null; isLocalOnly?: boolean }>(), { user: null, isLocalOnly: false });
const emit = defineEmits<{ (e: 'submenu-action', action: string): void }>();
const appStore = useAppStore();
const shell = useShellStore();
const router = useRouter();

/** Scoped SSO session (Odoo B- app): modules flat, no category flyouts, no account module children. */
const scope = getSsoScope();
const focus = scope !== null;
/** In focus mode only modules whose permission id is in the token's scope survive (the server already narrows availableApps; this enforces it here too). */
const inScope = (m: ModuleConfig) => !focus || scope!.includes(m.moduleId);

const items = computed<ResolvedSidebarItem[]>(() => {
  const available = new Set(appStore.availableApps.map((a) => a.id));
  const out: ResolvedSidebarItem[] = [];
  for (const item of appStore.resolvedSidebar) {
    if (item.type === 'module') {
      if (available.has(item.module.id) && inScope(item.module)) out.push(item);
      continue;
    }
    const children = item.children.filter((c) => available.has(c.id) && inScope(c));
    if (focus) { for (const c of children) out.push({ type: 'module', module: c }); }
    else if (children.length) out.push({ ...item, children });
  }
  return out;
});
const currentId = computed(() => appStore.currentAppId);
/**
 * Round 6: "I still don't see the yellow overdue orders in the left side menu; you can remove the tile from
 * the top right." A module's own attention registration (`composables/useModuleAttention` — the Production
 * Scheduler's overdue orders, Monitoring's ops figure) is what the badge shows; `shell.badges` (the ops
 * summary's `problems` / `openProposals`) stays the fallback for modules that register nothing of their own.
 * A registration survives its module's unmount as `stale`, and the badge keeps showing that last count —
 * hence `?? `, not `||`: a registered 0 is "all clear" and correctly hides the badge, rather than falling
 * through to the summary figure. Clicking the item just navigates, as it always did.
 */
const badge = (m: ModuleConfig) => shell.moduleAttention[m.id]?.count ?? shell.badges[m.id] ?? 0;
function go(m: ModuleConfig) { void router.push(`/${m.slug}`); }

// ── account block ────────────────────────────────────────────────────────────
const userLabel = computed(() => props.user?.attributes?.email || props.user?.username || 'Local User');
const initials = computed(() => { const s = String(userLabel.value); const parts = s.replace(/@.*/, '').split(/[.\s_-]+/).filter(Boolean); return (parts.length >= 2 ? parts[0][0] + parts[1][0] : s.slice(0, 2)).toUpperCase(); });
/** Everything already reachable from the list above — those modules are not repeated in the account menu. */
const listedIds = computed(() => {
  const s = new Set<string>();
  for (const item of items.value) { if (item.type === 'module') s.add(item.module.id); else for (const c of item.children) s.add(c.id); }
  return s;
});
const accountChildren = computed(() => {
  if (focus) return [];
  const available = new Set(appStore.availableApps.map((a) => a.id));
  return appStore.resolvedAccount.children.filter((c) => available.has(c.id) && !listedIds.value.has(c.id));
});
const accountActions = computed(() => appStore.resolvedAccount.actions.filter((a) => a.enabled && (a.id !== 'switch-user' || props.isLocalOnly)));

const accountOpen = ref(false);
/** The account button + its menu; a document click outside it closes the menu (clicks inside are its own). */
const acctRoot = ref<HTMLElement | null>(null);
function closeAccount() { accountOpen.value = false; }
function onDocClick(e: MouseEvent) { if (!acctRoot.value?.contains(e.target as Node)) closeAccount(); }
function toggleAccount() { accountOpen.value = !accountOpen.value; }
function goModule(slug: string) { closeAccount(); void router.push(`/${slug}`); }
function act(id: string) { closeAccount(); emit('submenu-action', id); }
function onKey(e: KeyboardEvent) { if (e.key === 'Escape') closeAccount(); }
onMounted(() => { window.addEventListener('keydown', onKey); document.addEventListener('click', onDocClick); });
onBeforeUnmount(() => { window.removeEventListener('keydown', onKey); document.removeEventListener('click', onDocClick); });
</script>

<template>
  <nav id="app-sidebar" class="app-sidebar" :class="{ collapsed: shell.collapsed }" aria-label="Modules">
    <div class="scroll">
      <template v-for="item in items" :key="item.type === 'module' ? item.module.id : item.id">
        <template v-if="item.type === 'category'">
          <div class="sec" :class="{ dev: item.devOnly }"><span>{{ item.label }}</span><span v-if="item.devOnly" class="dev-tag">dev</span></div>
          <a v-for="m in item.children" :key="m.id" href="#" class="item" :class="{ on: m.id === currentId }" :title="m.name" :aria-current="m.id === currentId ? 'page' : undefined" @click.prevent="go(m)">
            <img :src="m.iconUrl" alt="" class="ico" /><span class="label">{{ m.name }}</span><span v-if="badge(m)" class="count">{{ badge(m) }}</span>
          </a>
        </template>
        <a v-else href="#" class="item" :class="{ on: item.module.id === currentId }" :title="item.module.name" :aria-current="item.module.id === currentId ? 'page' : undefined" @click.prevent="go(item.module)">
          <img :src="item.module.iconUrl" alt="" class="ico" /><span class="label">{{ item.module.name }}</span><span v-if="badge(item.module)" class="count">{{ badge(item.module) }}</span>
        </a>
      </template>
      <p v-if="!items.length" class="empty">No modules available for your account.</p>
    </div>
    <div class="foot">
      <div ref="acctRoot" class="acct">
        <button type="button" class="acct-btn" :class="{ on: accountOpen }" :title="userLabel" :aria-expanded="accountOpen" :aria-haspopup="true" @click="toggleAccount">
          <span class="avatar">{{ initials }}</span><span class="label">{{ userLabel }}</span>
        </button>
        <div v-if="accountOpen" class="acct-menu" role="menu">
          <div class="who">{{ userLabel }}</div>
          <div v-for="m in accountChildren" :key="m.id" class="row" role="menuitem" tabindex="0" @click="goModule(m.slug)" @keydown.enter="goModule(m.slug)">{{ m.name }}</div>
          <div v-if="accountChildren.length && accountActions.length" class="hr"></div>
          <div v-for="a in accountActions" :key="a.id" class="row" role="menuitem" tabindex="0" @click="act(a.id)" @keydown.enter="act(a.id)">{{ a.label }}</div>
        </div>
      </div>
      <button type="button" class="toggle" :title="shell.collapsed ? 'Expand sidebar' : 'Collapse sidebar'" :aria-expanded="!shell.collapsed" @click="shell.toggleCollapsed()">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path :d="shell.collapsed ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <span class="label">{{ shell.collapsed ? 'Expand' : 'Collapse' }}</span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
.app-sidebar { width: var(--shell-sidebar-w); background: var(--shell); color: var(--shell-ink); display: flex; flex-direction: column; height: 100%; min-height: 0; transition: width .18s ease; position: relative; z-index: 20; }
.app-sidebar.collapsed { width: var(--shell-sidebar-w-collapsed); }
.scroll { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 8px 8px 12px; }
.sec { margin: 12px 10px 4px; font-family: var(--font-family-mono); font-size: 11px; letter-spacing: .12em; text-transform: uppercase; color: var(--shell-muted); white-space: nowrap; display: flex; gap: 6px; align-items: center; }
.dev-tag { font-size: 9.5px; padding: 0 5px; border-radius: 3px; background: var(--warn); color: #1a1408; letter-spacing: .04em; }
.collapsed .sec { margin: 12px 4px 4px; height: 1px; background: var(--shell-line); overflow: hidden; color: transparent; }
.collapsed .sec span { display: none; }
.item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 7px; color: var(--shell-ink); text-decoration: none; font-size: 15px; white-space: nowrap; position: relative; }
.item:hover { background: var(--shell-hover); } .item.on { background: var(--shell-active); font-weight: 600; }
.item.on::before { content: ""; position: absolute; left: -8px; top: 8px; bottom: 8px; width: 3px; border-radius: 2px; background: var(--accent); }
.ico { width: 20px; height: 20px; flex: 0 0 auto; opacity: .92; filter: var(--shell-icon-filter); }
.count { margin-left: auto; font-family: var(--font-family-mono); font-size: 11.5px; padding: 1px 7px; border-radius: 999px; background: var(--warn); color: #1a1408; }
.collapsed .item { justify-content: center; padding: 9px 0; } .collapsed .item .label, .collapsed .item .count { display: none; }
.collapsed .item:hover::after { content: attr(title); position: absolute; left: calc(100% + 10px); background: var(--ink); color: var(--panel); padding: 4px 9px; border-radius: 6px; font-size: 13px; white-space: nowrap; z-index: 20; box-shadow: var(--shell-shadow); }
.empty { color: var(--shell-muted); font-size: 13px; padding: 8px 10px; }
.foot { border-top: 1px solid var(--shell-line); padding: 8px; }
.acct { position: relative; }
.acct-btn { display: flex; align-items: center; gap: 10px; width: 100%; padding: 6px 8px; border: 0; border-radius: 7px; background: none; color: var(--shell-ink); font: inherit; font-size: 14px; text-align: left; cursor: pointer; overflow: hidden; }
.acct-btn:hover, .acct-btn.on { background: var(--shell-hover); }
.acct-btn .label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Shell tokens, not --accent-soft/--accent: the column is dark in every theme, so the chip has to take its
   contrast from the column it sits on rather than from the page palette. */
.avatar { flex: 0 0 auto; width: 28px; height: 28px; border-radius: 50%; background: var(--shell-active); color: var(--shell-ink); border: 1px solid var(--shell-line); display: grid; place-items: center; font-size: 12px; font-weight: 600; }
.collapsed .acct-btn { justify-content: center; padding: 6px 0; } .collapsed .acct-btn .label { display: none; }
.acct-menu { position: absolute; left: 0; bottom: calc(100% + 8px); min-width: 220px; z-index: 40; background: var(--panel); color: var(--ink); border: 1px solid var(--line); border-radius: 10px; box-shadow: var(--shell-shadow); padding: 8px; font-size: 14px; }
.who { padding: 6px 8px 8px; color: var(--muted); font-size: 13px; border-bottom: 1px solid var(--line); margin-bottom: 4px; word-break: break-all; }
.row { padding: 7px 8px; border-radius: 6px; cursor: pointer; white-space: nowrap; } .row:hover, .row:focus-visible { background: var(--panel-2); outline: none; }
.hr { height: 1px; background: var(--line); margin: 4px 0; }
.toggle { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 10px; border: 0; border-radius: 7px; background: none; color: var(--shell-muted); font: inherit; font-size: 14px; cursor: pointer; }
.toggle:hover { background: var(--shell-hover); color: var(--shell-ink); } .collapsed .toggle { justify-content: center; } .collapsed .toggle .label { display: none; }
.toggle:focus-visible, .item:focus-visible, .acct-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .app-sidebar { transition: none; } }
</style>
